import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { X as CloseIcon } from 'react-feather';
import { useQuery } from '@apollo/client';
import { useFilteredSearchResults } from '../providers/FilteredSearchResultsProvider';
import { priceCategoryLabel } from '@pv/common/utils';
import { amenitiesQuery } from '../graphql';
import { SpaceAmenitiesQuery } from '@pv/common/graphql/operations';

interface SearchFiltersDialogProps {
  open: boolean;
  onClose: () => void;
}

interface Amenity {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
}

export const SearchFiltersDialog: FC<SearchFiltersDialogProps> = ({
  open,
  onClose,
}) => {
  const {
    liveAvailabilityFilter,
    setLiveAvailabilityFilter,
    amenityFilters,
    setAmenityFilters,
    priceCategoryFilter,
    setPriceCategoryFilter,
  } = useFilteredSearchResults();

  const { data: amenitiesData } = useQuery<SpaceAmenitiesQuery>(amenitiesQuery);

  const [filters, setFilters] = useState({
    liveAvailability: liveAvailabilityFilter,
    amenityIds: amenityFilters,
    priceCategory: priceCategoryFilter,
  });

  useEffect(() => {
    setFilters({
      liveAvailability: liveAvailabilityFilter,
      amenityIds: amenityFilters,
      priceCategory: priceCategoryFilter,
    });
  }, [liveAvailabilityFilter, amenityFilters, priceCategoryFilter]);

  const handleClearAll = () => {
    setFilters({
      liveAvailability: false,
      amenityIds: [],
      priceCategory: {
        affordable: false,
        mid_range: false,
        luxury: false,
      },
    });
  };

  const handleApply = () => {
    setLiveAvailabilityFilter(filters.liveAvailability);
    setAmenityFilters(filters.amenityIds);
    setPriceCategoryFilter(filters.priceCategory);
    onClose();
  };

  const handleAmenityToggle = (amenityId: string, checked: boolean) => {
    setFilters({
      ...filters,
      amenityIds: checked
        ? [...filters.amenityIds, amenityId]
        : filters.amenityIds.filter((id) => id !== amenityId),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          Filters
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          size="small"
        >
          <CloseIcon size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: '20px' }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="column">
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Price
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                See only venues within your price range.
              </Typography>
            </Stack>
            <Stack direction="row" gap={1}>
              <FormControlLabel
                control={<Checkbox />}
                label={priceCategoryLabel('affordable')}
                checked={filters.priceCategory.affordable}
                onChange={() => {
                  setFilters({
                    ...filters,
                    priceCategory: {
                      ...filters.priceCategory,
                      affordable: !filters.priceCategory.affordable,
                    },
                  });
                }}
              />
              <FormControlLabel
                control={<Checkbox />}
                label={priceCategoryLabel('mid_range')}
                checked={filters.priceCategory.mid_range}
                onChange={() => {
                  setFilters({
                    ...filters,
                    priceCategory: {
                      ...filters.priceCategory,
                      mid_range: !filters.priceCategory.mid_range,
                    },
                  });
                }}
              />
              <FormControlLabel
                control={<Checkbox />}
                label={priceCategoryLabel('luxury')}
                checked={filters.priceCategory.luxury}
                onChange={() => {
                  setFilters({
                    ...filters,
                    priceCategory: {
                      ...filters.priceCategory,
                      luxury: !filters.priceCategory.luxury,
                    },
                  });
                }}
              />
            </Stack>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="column">
              <label htmlFor="live-availability-filter">
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Live availability
                </Typography>
              </label>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                See only venues with real-time availability.
              </Typography>
            </Stack>
            <Checkbox
              id="live-availability-filter"
              checked={filters.liveAvailability}
              onChange={(e) =>
                setFilters({ ...filters, liveAvailability: e.target.checked })
              }
              color="primary"
            />
          </Stack>

          {amenitiesData?.amenities && amenitiesData.amenities.length > 0 && (
            <>
              {amenitiesData.amenities.map((amenity) => (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  key={amenity.slug}
                >
                  <Stack direction="column">
                    <Typography variant="subtitle1" fontWeight={600}>
                      {amenity.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {amenity.description}
                    </Typography>
                  </Stack>
                  <Checkbox
                    checked={filters.amenityIds.includes(amenity.id)}
                    onChange={(e) =>
                      handleAmenityToggle(amenity.id, e.target.checked)
                    }
                    color="primary"
                  />
                </Stack>
              ))}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        <Button
          onClick={handleClearAll}
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          Clear all
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          color="primary"
          sx={{
            textTransform: 'none',
            px: 4,
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
